import { useState } from "react";
import {
  PLATFORM_COST_CADENCE_LABELS,
  PLATFORM_COST_CADENCES,
  PLATFORM_COST_CATEGORIES,
  PLATFORM_COST_CATEGORY_LABELS,
  parseDollarsToCents,
  type PlatformCostCadence,
  type PlatformCostCategory,
  type PlatformCostDraft,
} from "@/lib/platform/platformCostLedger";

const fieldClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-100";

export function PlatformSubscriptionCostForm(props: {
  title: string;
  submitLabel: string;
  hint?: string;
  initial: PlatformCostDraft;
  initialAmount: string;
  showEndedOn: boolean;
  busy: boolean;
  onCancel: () => void;
  onSubmit: (draft: PlatformCostDraft) => void;
}) {
  const [vendor, setVendor] = useState(props.initial.vendor);
  const [productName, setProductName] = useState(props.initial.product_name);
  const [category, setCategory] = useState<PlatformCostCategory>(props.initial.category);
  const [amount, setAmount] = useState(props.initialAmount);
  const [cadence, setCadence] = useState<PlatformCostCadence>(props.initial.cadence);
  const [startedOn, setStartedOn] = useState(props.initial.started_on);
  const [endedOn, setEndedOn] = useState(props.initial.ended_on ?? "");
  const [notes, setNotes] = useState(props.initial.notes ?? "");
  const [localError, setLocalError] = useState<string | null>(null);

  function submit() {
    const cents = parseDollarsToCents(amount);
    if (cents == null) {
      setLocalError("Enter an amount like 20.00 (two decimal places max).");
      return;
    }
    setLocalError(null);
    props.onSubmit({
      vendor,
      product_name: productName,
      category,
      amount_cents: cents,
      cadence,
      started_on: startedOn,
      ended_on: props.showEndedOn && endedOn ? endedOn : null,
      notes: notes.trim() ? notes : null,
    });
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{props.title}</h3>
        {props.hint ? <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">{props.hint}</p> : null}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
          Vendor
          <input className={`${fieldClass} mt-1`} value={vendor} onChange={(e) => setVendor(e.target.value)} required />
        </label>
        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
          Product
          <input
            className={`${fieldClass} mt-1`}
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            required
          />
        </label>
        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
          Category
          <select
            className={`${fieldClass} mt-1`}
            value={category}
            onChange={(e) => setCategory(e.target.value as PlatformCostCategory)}
          >
            {PLATFORM_COST_CATEGORIES.map((id) => (
              <option key={id} value={id}>
                {PLATFORM_COST_CATEGORY_LABELS[id]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
          Amount (USD)
          <input
            className={`${fieldClass} mt-1`}
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="20.00"
            required
          />
        </label>
        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
          Cadence
          <select
            className={`${fieldClass} mt-1`}
            value={cadence}
            onChange={(e) => setCadence(e.target.value as PlatformCostCadence)}
          >
            {PLATFORM_COST_CADENCES.map((id) => (
              <option key={id} value={id}>
                {PLATFORM_COST_CADENCE_LABELS[id]}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
          Starts
          <input
            className={`${fieldClass} mt-1`}
            type="date"
            value={startedOn}
            onChange={(e) => setStartedOn(e.target.value)}
            required
          />
        </label>
        {props.showEndedOn ? (
          <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
            Ends
            <input className={`${fieldClass} mt-1`} type="date" value={endedOn} onChange={(e) => setEndedOn(e.target.value)} />
          </label>
        ) : null}
      </div>
      <label className="block text-xs font-medium text-gray-600 dark:text-zinc-400">
        Notes
        <textarea
          className={`${fieldClass} mt-1`}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>
      {localError ? <p className="text-sm text-red-700 dark:text-red-300">{localError}</p> : null}
      <div className="flex flex-wrap gap-2">
        <button
          type="submit"
          disabled={props.busy}
          className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
        >
          {props.busy ? "Saving…" : props.submitLabel}
        </button>
        <button
          type="button"
          onClick={props.onCancel}
          disabled={props.busy}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-zinc-600 dark:bg-zinc-900 dark:text-zinc-200"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
